// Upscale manager (M13). The upscaled pixels of a photo are a cache, never an edit of the original:
// `settings.upscale` says what is wanted; this module finds the PNG in memory → IndexedDB → or runs the
// upscale worker, and hands it to the viewer and to every export as the photo's source.
import * as Comlink from 'comlink';
import { create } from 'zustand';
import { createStore, delMany, get, set, type UseStore } from 'idb-keyval';
import { guard } from '../../io/workerGuard';
import { touchLru, LRU_KEY, type LruIndex } from '../background/matteLru';
import { useEditor } from '../../state/editor';
import { useLibrary, type LibraryPhoto } from '../../state/library';
import type { Settings } from '../../model/types';
import type { UpscaleJob, UpscaleWorkerApi } from './upscale.worker';
import { targetSize } from './upscaleMath';
import type { UpscaleModelId } from './models';

/** Each entry is a PNG of up to 24 MP (~20–50 MB): keep few. */
const IDB_MAX = 6;
const MEMORY_MAX = 2;

type Status = { state: 'running' | 'ready' | 'error'; done: number; total: number; message?: string };
export type UpscaleState = {
  status: Record<string, Status>;
  /** Bumped whenever an upscaled source becomes available (the viewer re-decodes). */
  version: number;
  available: UpscaleModelId[] | null;
  lastRun: {
    ep: string;
    inferMs: number;
    totalMs: number;
    tiles: number;
    width: number;
    height: number;
  } | null;
};
export const useUpscale = create<UpscaleState>(() => ({
  status: {},
  version: 0,
  available: null,
  lastRun: null,
}));
export const upscaleStats = { runs: 0, cacheHits: 0 };

let worker: Comlink.Remote<UpscaleWorkerApi> | null = null;
export function upscaleWorker(): Comlink.Remote<UpscaleWorkerApi> {
  if (!worker) {
    const raw = new Worker(new URL('./upscale.worker.ts', import.meta.url), {
      type: 'module',
      name: 'upscale',
    });
    worker = guard(raw, Comlink.wrap<UpscaleWorkerApi>(raw), () => {
      raw.terminate();
      worker = null;
    });
  }
  return worker;
}

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-upscaled', 'upscaled'));
const memory = new Map<string, Blob>();
const remember = (k: string, b: Blob) => {
  memory.delete(k);
  memory.set(k, b);
  while (memory.size > MEMORY_MAX) memory.delete(memory.keys().next().value as string);
};
async function storeBlob(key: string, b: Blob) {
  try {
    await set(key, b, store());
    const { index, evict } = touchLru(
      (await get<LruIndex>(LRU_KEY, store())) ?? [],
      key,
      Date.now(),
      IDB_MAX,
    );
    await set(LRU_KEY, index, store());
    if (evict.length) await delMany(evict, store());
  } catch {
    /* storage unavailable: the memory cache and a re-run still work */
  }
}

function fnv(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
export const upscaleKey = (p: LibraryPhoto, u: Settings['upscale']) =>
  `up-${u.model}-${u.factor}-${Math.round(u.strength)}-${fnv(`${p.name}|${p.size}|${p.lastModified}`)}`;

const isOn = (s: Settings | undefined) => !!s && s.upscale.factor > 1;

/**
 * The size "Make bigger" would give (null = already too big). `photo.width/height` always hold the
 * ORIGINAL's size (the viewer only records sizes it decoded from the original file); 0 = not known yet.
 */
export function upscalePreview(p: LibraryPhoto, factor: number) {
  if (!p.width || !p.height) return { width: 0, height: 0, factor };
  return targetSize(p.width, p.height, factor);
}

const setStatus = (id: string, st: Status) =>
  useUpscale.setState((s) => ({ status: { ...s.status, [id]: st } }));

export async function checkUpscaleModels(): Promise<UpscaleModelId[]> {
  const cur = useUpscale.getState().available;
  if (cur) return cur;
  const a = await upscaleWorker()
    .available()
    .catch(() => [] as UpscaleModelId[]);
  useUpscale.setState({ available: a });
  return a;
}

/** Synchronous: the upscaled source if it is already in memory. */
export function upscaledInMemory(p: LibraryPhoto, s: Settings | undefined): Blob | null {
  return isOn(s) ? (memory.get(upscaleKey(p, s!.upscale)) ?? null) : null;
}

const inflight = new Map<string, Promise<Blob>>();
/** The upscaled source of a photo (computed if needed), or null when upscale is off. */
export async function upscaledBlob(p: LibraryPhoto, s: Settings | undefined): Promise<Blob | null> {
  if (!isOn(s)) return null;
  const key = upscaleKey(p, s!.upscale);
  const hit = memory.get(key);
  if (hit) return hit;
  const running = inflight.get(key);
  if (running) return running;
  const job = (async () => {
    const stored = await get<Blob>(key, store()).catch(() => undefined);
    if (stored && stored.size) {
      upscaleStats.cacheHits++;
      remember(key, stored);
      return stored;
    }
    setStatus(p.id, { state: 'running', done: 0, total: 0 });
    try {
      const u = s!.upscale;
      const jobSpec: UpscaleJob = { model: u.model, factor: u.factor, strength: u.strength };
      const r = await upscaleWorker().upscaleFile(
        p.file,
        jobSpec,
        Comlink.proxy((done: number, total: number) => setStatus(p.id, { state: 'running', done, total })),
      );
      upscaleStats.runs++;
      const blob = new Blob([r.png as Uint8Array<ArrayBuffer>], { type: 'image/png' });
      remember(key, blob);
      await storeBlob(key, blob);
      if (!p.width) useLibrary.getState().patch(p.id, { width: r.srcWidth, height: r.srcHeight });
      useUpscale.setState((st) => ({
        lastRun: {
          ep: r.ep,
          inferMs: r.inferMs,
          totalMs: r.totalMs,
          tiles: r.tiles,
          width: r.width,
          height: r.height,
        },
        version: st.version + 1,
      }));
      setStatus(p.id, { state: 'ready', done: r.tiles, total: r.tiles });
      return blob;
    } catch (e) {
      setStatus(p.id, { state: 'error', done: 0, total: 0, message: (e as Error).message });
      throw e;
    }
  })().finally(() => inflight.delete(key));
  inflight.set(key, job);
  return job;
}

/** The pixels every export and the viewer should use: the upscaled PNG when upscale is on. */
export async function sourceFor(p: LibraryPhoto, s: Settings | undefined): Promise<Blob> {
  return (await upscaledBlob(p, s)) ?? p.file;
}

/** "Make bigger" (one undo step), then runs the upscale. factor 1 turns it off. */
export async function makeBigger(id: string, factor: 1 | 2 | 4, opts: Partial<Settings['upscale']> = {}) {
  useEditor.getState().update(id, (s) => {
    s.upscale = { ...s.upscale, ...opts, factor };
  });
  const p = useLibrary.getState().photos.find((x) => x.id === id);
  if (!p || factor === 1) return;
  try {
    await upscaledBlob(p, useEditor.getState().get(id));
  } catch {
    /* status carries the error */
  }
}

export const setUpscaleTestOptions = (o: { failLoad?: boolean; preferGpu?: boolean }) => {
  useUpscale.setState({ available: null });
  memory.clear();
  return upscaleWorker().setTestOptions(o);
};
